/**
 * Netvisor accounting settings hooks (#3022). Types live here, as for the other
 * integrations; the contract is `routes/integrations-netvisor.ts` in staff-api.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type NetvisorLineKind = 'labor' | 'part' | 'service' | 'surcharge' | 'other';
export const NETVISOR_LINE_KINDS: readonly NetvisorLineKind[] = ['labor', 'part', 'service', 'surcharge', 'other'];

export type NetvisorLanguage = 'FI' | 'EN' | 'SE';
export type NetvisorInvoiceStatus = 'open' | 'unsent';
export type NetvisorMissing = 'credentials' | 'vat_codes' | 'product_group' | 'test';

export interface NetvisorProductConfig {
  netvisorKey?: string;
  name?: string;
  code?: string;
  unit?: string;
  defaultVatPercent?: number;
  domesticAccountNumber?: string;
}

export interface NetvisorLastTest {
  testedAt: string;
  success: boolean;
  /** Stable code, e.g. `NETVISOR_TEST_UNAUTHORIZED`. */
  error?: string;
}

export interface NetvisorSettings {
  /** The platform offers the integration (partner credentials and host). */
  available: boolean;
  enabled: boolean;
  /** `ab…xyz`: the integration user id is never returned in full. */
  customerIdMasked: string | null;
  organisationId: string | null;
  language: NetvisorLanguage;
  hasCustomerKey: boolean;
  encryptionAvailable: boolean;
  lastTest: NetvisorLastTest | null;
  exportSince: string | null;
  invoiceStatus: NetvisorInvoiceStatus;
  productGroup: string | null;
  products: Partial<Record<NetvisorLineKind, NetvisorProductConfig>>;
  /** VAT percent (`"25.5"`) to Netvisor VAT code. */
  vatCodes: Record<string, string>;
  updateExistingCustomers: boolean;
  missing: NetvisorMissing[];
  /** Complete, tested, on and offered by the platform. */
  ready: boolean;
}

export interface NetvisorAttentionItem {
  type: 'invoice' | 'customer' | 'product';
  id: string;
  /** Stable code such as `unknown_outcome:timeout`, `rejected:invalid_data`, `doc:vat_code_missing`. */
  reason: string | null;
  /** Invoice number, party type or line kind. */
  label: string;
  hasNetvisorKey: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NetvisorReviewItem {
  exportId: string;
  invoiceNumber: string;
  reconcileState: string;
  totalCents: number;
  paidCents: number;
  netvisorOpenSumCents: number | null;
  /** OS Buddy's own ledger total, when the invoice still exists. */
  localPaidCents: number | null;
  lastReconciledAt: string | null;
}

export interface NetvisorSummary {
  invoices: Record<string, number>;
  customers: Record<string, number>;
  products: Record<string, number>;
  reconcile: Record<string, number>;
  attentionCount: number;
  attention: NetvisorAttentionItem[];
  review: NetvisorReviewItem[];
}

export interface NetvisorOverview {
  settings: NetvisorSettings;
  /** A demo tenant or a test environment never reaches Netvisor, whatever is saved. */
  outboundBlocked: boolean;
  summary: NetvisorSummary;
}

export interface NetvisorCredentialsInput {
  /** Omit to keep the stored integration user id. */
  customerId?: string;
  /** Omit to keep the stored key. */
  customerKey?: string;
  organisationId?: string;
  language?: NetvisorLanguage;
}

export interface NetvisorSettingsInput {
  invoiceStatus?: NetvisorInvoiceStatus;
  productGroup?: string | null;
  products?: Partial<Record<NetvisorLineKind, NetvisorProductConfig | null>>;
  vatCodes?: Record<string, string>;
  updateExistingCustomers?: boolean;
}

export interface NetvisorTestResponse {
  success: boolean;
  settings: NetvisorSettings;
}

export interface NetvisorResolveInput {
  type: NetvisorAttentionItem['type'];
  id: string;
  decision: 'retry' | 'bind' | 'abandon';
  netvisorKey?: string;
  confirmNotInNetvisor?: boolean;
}

const NETVISOR_QUERY_KEY = ['integrations', 'netvisor'] as const;

export function useNetvisorIntegration(enabled: boolean) {
  return useQuery({
    queryKey: NETVISOR_QUERY_KEY,
    queryFn: () => api.get<NetvisorOverview>('/integrations/netvisor'),
    enabled,
  });
}

function useNetvisorMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NETVISOR_QUERY_KEY });
    },
  });
}

export function useSaveNetvisorCredentials() {
  return useNetvisorMutation((input: NetvisorCredentialsInput) =>
    api.put<NetvisorSettings>('/integrations/netvisor/credentials', input),
  );
}

export function useSaveNetvisorSettings() {
  return useNetvisorMutation((input: NetvisorSettingsInput) =>
    api.put<NetvisorSettings>('/integrations/netvisor/settings', input),
  );
}

export function useTestNetvisorCredentials() {
  return useNetvisorMutation(() => api.post<NetvisorTestResponse>('/integrations/netvisor/test', {}));
}

export function useSetNetvisorEnabled() {
  return useNetvisorMutation((enabled: boolean) =>
    api.put<NetvisorSettings>('/integrations/netvisor/enabled', { enabled }),
  );
}

export function useDeleteNetvisorCredentials() {
  return useNetvisorMutation(() => api.delete<NetvisorSettings>('/integrations/netvisor/credentials'));
}

export function useResolveNetvisorItem() {
  return useNetvisorMutation((input: NetvisorResolveInput) =>
    api.post<{ id: string; status: string }>(
      `/integrations/netvisor/attention/${input.type}/${encodeURIComponent(input.id)}/resolve`,
      {
        decision: input.decision,
        ...(input.netvisorKey !== undefined ? { netvisorKey: input.netvisorKey } : {}),
        ...(input.confirmNotInNetvisor !== undefined ? { confirmNotInNetvisor: input.confirmNotInNetvisor } : {}),
      },
    ),
  );
}
